import React, { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Nav } from "@/components/layout/Nav";
import { Footer } from "@/components/layout/Footer";
import { PageSEO } from "@/components/seo/PageSEO";
import { getProductSlug, getProductPageTitle, getProductPageDescription, SITE_URL } from "@/utils/seo";
import { trackViewItem } from "@/utils/ga4";
import { API, BACKEND_URL } from "@/config";
import { ArrowRight, ChevronLeft, CheckCircle, Battery, Zap } from "lucide-react";

const SITE = SITE_URL;

const imgSrc = (url) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  return `${BACKEND_URL}${url.startsWith("/") ? "" : "/"}${url}`;
};

const yesNo = (v) => (v === true || v === "yes") ? "Yes" : v === false ? "No" : v ?? null;

/** Table row — renders nothing when value is null/undefined/"" */
const SpecRow = ({ label, value }) =>
  value != null && value !== "" && value !== "—" ? (
    <tr className="border-b border-[#FFD166]/8">
      <td className="py-2.5 text-[#B9C8D5] text-sm pr-6 w-44">{label}</td>
      <td className="py-2.5 text-[#F6F1E4] text-sm font-medium">{value}</td>
    </tr>
  ) : null;

export default function ECVProduct() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const viewFiredRef = useRef(null);

  useEffect(() => {
    // Fetch ECV products AND bundles in parallel
    Promise.all([
      axios.get(`${API}/products`),
      axios.get(`${API}/bundles`).catch(() => ({ data: { bundles: [] } })),
    ]).then(([pRes, bRes]) => {
      const allEcvProducts = (pRes.data.products || []).filter(
        (p) => p && p.category === "ecvs" && p.active !== false
      );
      const allEcvBundles = (bRes.data.bundles || [])
        .filter((b) => b && b.category === "ecvs" && b.active !== false)
        .map((b) => ({ ...b, is_bundle: true }));
      const all = [...allEcvProducts, ...allEcvBundles];

      // Match by seo_slug first, then by product id
      const p = all.find(
        (x) => (x.seo_slug && x.seo_slug === slug) || x.id === slug
      );
      if (!p) { navigate("/ecvs", { replace: true }); return; }

      setProduct(p);
      if (viewFiredRef.current !== p.id) {
        viewFiredRef.current = p.id;
        trackViewItem(p);
      }

      // Related: other active+visible ECVs (not the current one)
      setRelated(
        all.filter((x) => x.id !== p.id && x.website_visible !== false).slice(0, 3)
      );
    }).finally(() => setLoading(false));
  }, [slug, navigate]);

  /* ── Loading skeleton ──────────────────────────────────────────────── */
  if (loading) {
    return (
      <>
        <Nav />
        <div className="min-h-screen flex items-center justify-center" style={{ background: "#040A0F" }}>
          <div className="w-8 h-8 border-2 border-[#FFD166]/30 border-t-[#FFD166] rounded-full animate-spin" />
        </div>
      </>
    );
  }

  if (!product) return null;

  const ea = product.ecv_attributes || {};
  const img = imgSrc(product.image);
  const canonicalSlug = getProductSlug(product) || slug;
  const canonicalUrl = `${SITE}/ecvs/${canonicalSlug}`;

  const pageTitle = (product.seo_title && product.seo_title.trim())
    ? product.seo_title.trim()
    : getProductPageTitle(product);
  const pageDesc = (product.seo_description && product.seo_description.trim())
    ? product.seo_description.trim()
    : getProductPageDescription(product);

  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: pageTitle.replace(" | Enchanted Gear Rentals", ""),
    description: product.description || pageDesc,
    provider: { "@id": `${SITE}/#business` },
    areaServed: { "@type": "City", name: "Orlando" },
    ...(img ? { image: img } : {}),
    offers: {
      "@type": "Offer",
      priceCurrency: "USD",
      price: product.tier_1_3,
      availability: "https://schema.org/InStock",
      seller: { "@id": `${SITE}/#business` },
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
      { "@type": "ListItem", position: 2, name: "ECVs", item: `${SITE}/ecvs` },
      { "@type": "ListItem", position: 3, name: product.name, item: canonicalUrl },
    ],
  };

  const freeAcc = product.free_accessories || [];

  return (
    <>
      <PageSEO
        title={pageTitle}
        description={pageDesc}
        canonical={canonicalUrl}
        image={img}
        structuredData={[productSchema, breadcrumbSchema]}
      />
      <Nav />
      <main className="min-h-screen" style={{ background: "var(--color-bg, #040A0F)" }}>
        <div className="container max-w-6xl pt-24 pb-20">

          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="text-xs text-[#B9C8D5] mb-6 flex items-center gap-1.5">
            <Link to="/" className="hover:text-[#FFD166]">Home</Link>
            <span className="opacity-40">/</span>
            <Link to="/ecvs" className="hover:text-[#FFD166]">ECVs</Link>
            <span className="opacity-40">/</span>
            <span className="text-[#FFD166]">{product.name}</span>
          </nav>

          {/* Hero grid */}
          <div className="grid lg:grid-cols-2 gap-10 mb-16">
            {/* Image */}
            <div className="rounded-2xl overflow-hidden bg-[#0A1825] aspect-[4/3]">
              {img ? (
                <img
                  src={img}
                  alt={`${product.name} ECV rental near Walt Disney World`}
                  className="w-full h-full object-cover"
                  width="600"
                  height="450"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Battery className="w-20 h-20 text-[#FFD166]/15" />
                </div>
              )}
            </div>

            {/* Details */}
            <div className="flex flex-col">
              <span className="text-[10px] uppercase tracking-widest text-[#B9C8D5] border border-[#FFD166]/20 px-2 py-0.5 rounded-full w-fit mb-3">ECV</span>
              <h1 className="font-serif text-3xl md:text-4xl text-[#F6F1E4] mb-3">{product.name}</h1>
              {product.tagline && <p className="text-[#B9C8D5] text-lg mb-4">{product.tagline}</p>}

              {/* Pricing */}
              <div className="rounded-xl bg-[#0A1825] border border-[#FFD166]/15 p-4 mb-5">
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-[#FFD166] font-serif text-3xl font-bold">${product.tier_1_3}</span>
                  <span className="text-[#B9C8D5] text-sm">/day (1–3 days)</span>
                </div>
                {product.tier_4_7 && (
                  <p className="text-[#B9C8D5] text-xs">${product.tier_4_7}/day for 4–7 days</p>
                )}
                {product.tier_8_plus && (
                  <p className="text-[#B9C8D5] text-xs">${product.tier_8_plus}/day for 8+ days</p>
                )}
              </div>

              {/* CTA */}
              <Link to={`/?focus=${product.id}#trip-setup`}>
                <button className="btn-primary w-full mb-3" data-testid="ecv-product-book-cta">
                  Check Availability &amp; Reserve <ArrowRight className="inline w-4 h-4 ml-1" />
                </button>
              </Link>
              <Link to="/ecvs" className="flex items-center gap-1 text-xs text-[#B9C8D5] hover:text-[#FFD166] justify-center">
                <ChevronLeft className="w-3 h-3" /> Back to all ECVs
              </Link>

              {/* Free accessories */}
              {freeAcc.length > 0 && (
                <div className="mt-5">
                  <p className="text-xs text-[#B9C8D5] uppercase tracking-widest mb-2">Included free</p>
                  <ul className="space-y-1">
                    {freeAcc.map((a, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-[#F6F1E4]">
                        <CheckCircle className="w-3.5 h-3.5 text-[#FFD166] shrink-0" /> {a}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Description */}
          {product.description && (
            <section className="mb-12">
              <h2 className="font-serif text-2xl text-[#F6F1E4] mb-4">About this ECV</h2>
              <p className="text-[#B9C8D5] leading-relaxed">{product.description}</p>
            </section>
          )}

          {/* ECV Specifications */}
          <section className="mb-12">
            <h2 className="font-serif text-2xl text-[#F6F1E4] mb-4">Specifications</h2>
            <div className="rounded-xl bg-[#0A1825] border border-[#FFD166]/10 overflow-hidden">
              <table className="w-full px-6">
                <tbody className="divide-y divide-[#FFD166]/8">
                  <SpecRow label="Type" value="ECV (Electric Convenience Vehicle)" />
                  {ea.battery_life_hours != null && (
                    <SpecRow label="Battery life" value={`${ea.battery_life_hours} hours`} />
                  )}
                  {ea.range_miles != null && (
                    <SpecRow label="Range" value={`${ea.range_miles} miles`} />
                  )}
                  {ea.max_weight_lbs != null && (
                    <SpecRow label="Weight capacity" value={`${ea.max_weight_lbs} lbs`} />
                  )}
                  {ea.top_speed_mph != null && (
                    <SpecRow label="Top speed" value={`${ea.top_speed_mph} mph`} />
                  )}
                  {ea.charge_time_hours != null && (
                    <SpecRow label="Charge time" value={`${ea.charge_time_hours} hours`} />
                  )}
                  {ea.weight_lbs != null && (
                    <SpecRow label="ECV weight" value={`${ea.weight_lbs} lbs`} />
                  )}
                  {ea.turning_radius_ft != null && (
                    <SpecRow label="Turning radius" value={`${ea.turning_radius_ft} ft`} />
                  )}
                  {ea.seat_type && <SpecRow label="Seat type" value={ea.seat_type} />}
                  {ea.color && <SpecRow label="Color" value={ea.color} />}
                  {ea.foldable != null && <SpecRow label="Foldable" value={yesNo(ea.foldable)} />}
                  {ea.has_basket != null && <SpecRow label="Storage basket" value={yesNo(ea.has_basket)} />}
                  {ea.armrests != null && <SpecRow label="Armrests" value={yesNo(ea.armrests)} />}
                  {ea.canopy != null && <SpecRow label="Canopy / sun shade" value={yesNo(ea.canopy)} />}
                </tbody>
              </table>
            </div>
          </section>

          {/* Sanitization note */}
          <section className="mb-12 rounded-xl bg-[#0A1825] border border-[#FFD166]/15 p-6">
            <div className="flex items-start gap-3">
              <Zap className="w-5 h-5 text-[#FFD166] mt-0.5 shrink-0" />
              <div>
                <h2 className="font-serif text-xl text-[#F6F1E4] mb-2">Fully charged and ready to ride</h2>
                <p className="text-[#B9C8D5] text-sm leading-relaxed">
                  Every ECV is delivered with a full charge, cleaned between rentals, and inspected before reaching your family. We include the charger so you can top up overnight at your resort.
                </p>
              </div>
            </div>
          </section>

          {/* Related ECVs */}
          {related.length > 0 && (
            <section className="mb-12">
              <h2 className="font-serif text-2xl text-[#F6F1E4] mb-6">Other ECVs you might like</h2>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-5">
                {related.map((r) => {
                  const rSlug = getProductSlug(r);
                  const rImg = imgSrc(r.image);
                  return (
                    <Link
                      key={r.id}
                      to={`/ecvs/${rSlug}`}
                      className="group rounded-xl bg-[#0A1825] border border-[#FFD166]/10 hover:border-[#FFD166]/40 overflow-hidden transition-all"
                      data-testid={`related-ecv-${r.id}`}
                    >
                      <div className="h-32 bg-[#060f18] overflow-hidden">
                        {rImg ? (
                          <img src={rImg} alt={r.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Battery className="w-8 h-8 text-[#FFD166]/15" />
                          </div>
                        )}
                      </div>
                      <div className="p-4">
                        <p className="font-serif text-[#F6F1E4] group-hover:text-[#FFD166] transition-colors">{r.name}</p>
                        <p className="text-[#FFD166] text-sm mt-1">From ${r.tier_1_3}/day</p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}
